import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["message", "reviewsCount", "rating", "large", "position", "interactive", "onRatingChange", "data-test"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useRef, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { StarIcon } from '@sainsburys-tech/bolt-icons';
import * as Styled from './Ratings.styles';
var defaultSize = {
  height: '15px',
  width: '17px'
};
var largeSize = {
  height: '22px',
  width: '28px'
};
var numberOfStars = 5;
var percentageStarToBoxWidth = 17.531746;
var interStarSpacing = (100 - numberOfStars * percentageStarToBoxWidth) / (numberOfStars - 1);

var calculateWidth = function calculateWidth(rating) {
  var width = rating * percentageStarToBoxWidth + interStarSpacing * Math.floor(rating);
  return Math.min(100, width);
};

var Ratings = function Ratings(_ref) {
  var message = _ref.message,
      reviewsCount = _ref.reviewsCount,
      initialRating = _ref.rating,
      large = _ref.large,
      position = _ref.position,
      interactive = _ref.interactive,
      onRatingChange = _ref.onRatingChange,
      dataTest = _ref['data-test'],
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(initialRating || 0),
      _useState2 = _slicedToArray(_useState, 2),
      rating = _useState2[0],
      setRating = _useState2[1];

  var _useState3 = useState(calculateWidth(initialRating || 0)),
      _useState4 = _slicedToArray(_useState3, 2),
      width = _useState4[0],
      setWidth = _useState4[1];

  var _useState5 = useState(Array(numberOfStars).fill('empty')),
      _useState6 = _slicedToArray(_useState5, 2),
      types = _useState6[0],
      setTypes = _useState6[1];

  var initialUpdate = useRef(true);
  useEffect(function () {
    if (initialUpdate.current) {
      initialUpdate.current = false;
      return;
    }

    if (typeof onRatingChange === 'function') {
      onRatingChange(rating);
    }

    setWidth(calculateWidth(rating));
  }, [rating]); // Update the state when the "rating" prop changes

  useEffect(function () {
    setRating(initialRating);
  }, [initialRating]);
  var starSize = large ? largeSize : defaultSize;
  var defaultMessage = !!reviewsCount && /*#__PURE__*/React.createElement(Styled.Count, {
    "data-test": "".concat(dataTest, "-ratingCount"),
    itemProp: "ratingCount"
  }, /*#__PURE__*/React.createElement("small", null, "(", reviewsCount, ")"));

  var renderStars = function renderStars(type) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, _toConsumableArray(Array(numberOfStars)).map(function (star, index) {
      var ratingValue = index + 1;
      var starPluralized = "".concat(ratingValue, " Star").concat(ratingValue === 1 ? '' : 's');

      var starProps = _objectSpread({
        key: index,
        type: type
      }, starSize);

      if (interactive) {
        var starCurrentTypes = function starCurrentTypes(value) {
          var currentTypes = types.map(function (_, typeIndex) {
            return typeIndex < value ? 'full' : 'empty';
          });
          setTypes(currentTypes);
        };

        starProps = _objectSpread(_objectSpread({}, starProps), {}, {
          type: types[index],
          onClick: function onClick() {
            starCurrentTypes(ratingValue);
            setRating(ratingValue);
          },
          onMouseEnter: function onMouseEnter() {
            starCurrentTypes(ratingValue);
          },
          onMouseLeave: function onMouseLeave() {
            starCurrentTypes(rating);
          }
        });
        var id = "star".concat(ratingValue);
        return /*#__PURE__*/React.createElement(Fragment, {
          key: id
        }, /*#__PURE__*/React.createElement(Styled.RadioInput, {
          value: ratingValue,
          id: id,
          name: "rating",
          className: "sr-only",
          "data-test": "".concat(dataTest, "-interactive-ratings"),
          onClick: function onClick() {
            starCurrentTypes(ratingValue);
            setRating(ratingValue);
          }
        }), /*#__PURE__*/React.createElement(Styled.Label, {
          htmlFor: id
        }, /*#__PURE__*/React.createElement("span", {
          className: "sr-only"
        }, starPluralized), /*#__PURE__*/React.createElement(StarIcon, starProps)));
      }

      return /*#__PURE__*/React.createElement(StarIcon, starProps);
    }));
  };

  return /*#__PURE__*/React.createElement(Styled.Wrapper, _extends({
    position: position,
    "data-star-rating": rating
  }, props, {
    "data-test": dataTest
  }), /*#__PURE__*/React.createElement(Styled.Stars, {
    "data-test": "".concat(dataTest, "-starContainer"),
    resize: !large,
    interactive: interactive
  }, /*#__PURE__*/React.createElement("div", {
    className: "sr-only"
  }, "Rating", ' ', /*#__PURE__*/React.createElement("span", {
    "data-test": "".concat(dataTest, "-ratingValue"),
    itemProp: "ratingValue"
  }, rating), ' ', "out of 5"), interactive ? /*#__PURE__*/React.createElement(React.Fragment, null, renderStars()) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Styled.EmptyStars, {
    "data-test": "".concat(dataTest, "-emptyStars")
  }, renderStars('empty')), /*#__PURE__*/React.createElement(Styled.FilledStars, {
    "data-test": "".concat(dataTest, "-filledStars"),
    style: {
      width: "".concat(width, "%")
    }
  }, renderStars('full')))), message || defaultMessage);
};

Ratings.displayName = 'Ratings';
Ratings.propTypes = {
  /**
   * The overall rating for the product
   */
  rating: PropTypes.number.isRequired,

  /**
   * The number of reviews the product has had
   */
  reviewsCount: PropTypes.number.isRequired,

  /**
   * Toggle large or default sizes of rating icons
   */
  large: PropTypes.bool,

  /**
   * Alignment of the rating, left, spaceBetween, right
   */
  position: PropTypes.oneOf(['left', 'spaceBetween', 'right']),

  /**
   * Override the message at the end of the rating
   */
  message: PropTypes.string,

  /**
   * Static or interactive rating
   */
  interactive: PropTypes.bool,

  /**
   * A callback to be called after rating change
   */
  onRatingChange: PropTypes.func,

  /**
   * Specify an optional className to be added to your Rating
   */
  className: PropTypes.string,

  /**
   * Specify an optional data-test attribute to be added to your Rating for e2e testing
   */
  'data-test': PropTypes.string
};
Ratings.defaultProps = {
  large: false,
  position: 'left',
  interactive: false,
  onRatingChange: null,
  'data-test': 'component-ratings'
};
export default Ratings;